import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LandingNavbar from './common/LandingNavbar';
import { openTawkChat } from './common/TawkTo';
import './LandingPage.css';
import './ContactPage.css';

const SUPPORT_EMAIL = 'support@syllera.com';
const INFO_EMAIL = 'info@syllera.com';

const ContactPage = ({ isSupport = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('landing');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const body = [formData.message, '', formData.name, formData.email].join('\n');
    const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  };

  return (
    <div className="contact-page">
      <LandingNavbar />

      <section className="contact-hero">
        <div className="contact-hero-content">
          <h1>{isSupport ? t('contactPage.supportTitle') : t('contactPage.title')}</h1>
          <p>{isSupport ? t('contactPage.supportSubtitle') : t('contactPage.subtitle')}</p>
        </div>
      </section>

      <section className="contact-section">
        <div className="section-container">
          <div className="contact-grid">
            <div className="contact-form-container">
              <h2>{t('contactPage.formTitle')}</h2>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name">{t('contactPage.name')}</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder={t('contactPage.namePlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">{t('contactPage.email')}</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder={t('contactPage.emailPlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="subject">{t('contactPage.subject')}</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    placeholder={t('contactPage.subjectPlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="message">{t('contactPage.message')}</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="6"
                    placeholder={t('contactPage.messagePlaceholder')}
                  />
                </div>
                <button type="submit" className="btn-primary full-width">
                  {t('contactPage.send')}
                </button>
                <p className="contact-send-note">{t('contactPage.sendNote')}</p>
              </form>
            </div>

            <div className="contact-info">
              <h2>{t('contactPage.touchTitle')}</h2>
              <div className="contact-info-item">
                <div className="contact-icon">
                  <span className="material-icons">chat</span>
                </div>
                <div>
                  <h3>{t('contactPage.liveChat')}</h3>
                  <p>{t('contactPage.liveChatDesc')}</p>
                  <button type="button" className="btn-primary" onClick={openTawkChat} style={{ marginTop: 8 }}>
                    {t('contactPage.startChat')}
                  </button>
                </div>
              </div>
              <div className="contact-info-item">
                <div className="contact-icon">
                  <span className="material-icons">email</span>
                </div>
                <div>
                  <h3>{t('contactPage.emailTitle')}</h3>
                  <p><a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></p>
                  <p><a href={`mailto:${INFO_EMAIL}`}>{INFO_EMAIL}</a></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-section">
            <div className="footer-logo">
              <span className="material-icons">school</span>
              <span>Syllera</span>
            </div>
            <p className="footer-description">
              {t('footer.tagline')}
            </p>
          </div>
          <div className="footer-section">
            <h4>{t('footer.product')}</h4>
            <ul>
              <li><a href="/#features" onClick={(e) => { e.preventDefault(); navigate('/#features'); }}>{t('nav.features')}</a></li>
              <li><a href="/#pricing" onClick={(e) => { e.preventDefault(); navigate('/#pricing'); }}>{t('nav.pricing')}</a></li>
              <li><a href="/#how-it-works" onClick={(e) => { e.preventDefault(); navigate('/#how-it-works'); }}>{t('nav.howItWorks')}</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>{t('footer.company')}</h4>
            <ul>
              <li><a href="/about">{t('footer.about')}</a></li>
              <li><a href="/contact">{t('footer.contact')}</a></li>
              <li><a href="/blog">{t('footer.blog')}</a></li>
              <li><a href="/partners">{t('footer.becomeAPartner')}</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>{t('footer.support')}</h4>
            <ul>
              <li><a href="/#faq" onClick={(e) => { e.preventDefault(); navigate('/#faq'); }}>{t('footer.faq')}</a></li>
              <li><a href="/help-center">{t('footer.helpCenter')}</a></li>
              <li><a href="/contact-support">{t('footer.contactSupport')}</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>{t('footer.copyright')}</p>
          <div className="footer-links">
            <a href="/privacy-policy">{t('footer.privacyPolicy')}</a>
            <a href="/terms-of-service">{t('footer.termsOfService')}</a>
            <a href="/cookie-policy">{t('footer.cookiePolicy')}</a>
            <a href="/refund-policy">{t('footer.refundPolicy')}</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
